import { useNavigate } from "react-router-dom";
import { Pencil, RotateCcw, Send, Star, Plus } from "lucide-react";
import SwipeableRow from "./SwipeableRow";
import { MEAL_TYPE_ICONS, MEAL_TYPE_LABELS, type LoggedMeal, type MealType } from "../lib/types";

interface Props {
  type: MealType;
  meals: LoggedMeal[];
  /** Recommended calorie budget for this meal section. Hides target UI if 0. */
  targetKcal?: number;
  /** Yesterday's meal of this type (for the "repeat yesterday" quick chip). */
  yesterdayMeal?: LoggedMeal | null;
  onAdd: (type: MealType) => void;
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
  onFavorite: (meal: LoggedMeal) => void;
  /** Called when the "Repeat yesterday's X" chip is tapped. */
  onLogAgain?: (meal: LoggedMeal) => void;
  /** Called when the "Share meal" chip is tapped — receives all logged meals
   *  in this section. Parent owns the modal state so it lives in one place. */
  onShareSection?: (meals: LoggedMeal[], label: string) => void;
}

export default function MealSection({ type, meals, targetKcal, yesterdayMeal, onAdd, onDelete, onEdit, onFavorite, onLogAgain, onShareSection }: Props) {
  const navigate = useNavigate();
  const totalKcal = meals.reduce((s, m) => s + m.estimate.totals.calories, 0);
  const showTarget = typeof targetKcal === "number" && targetKcal > 0;
  const delta = showTarget ? totalKcal - targetKcal! : 0;
  const isOver = delta > 0;
  const isNear = !isOver && totalKcal >= targetKcal! * 0.9;
  const statusColor = isOver
    ? "text-rose-400"
    : isNear
    ? "text-amber-400"
    : totalKcal > 0
    ? "text-emerald-400"
    : "text-slate-500";

  return (
    <section className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden">
      <header className="px-4 py-3 flex justify-between items-center border-b border-slate-800">
        <div className="flex items-baseline gap-2">
          <span className="text-base leading-none">{MEAL_TYPE_ICONS[type]}</span>
          <span className="text-sm font-medium text-slate-300">{MEAL_TYPE_LABELS[type]}</span>
        </div>
        <div className="text-right">
          <div className="tabular-nums leading-tight">
            <span className="text-base text-slate-100 font-semibold">{Math.round(totalKcal)}</span>
            {showTarget ? (
              <span className="text-[11px] text-slate-400"> / {targetKcal}</span>
            ) : (
              <span className="text-[11px] text-slate-400 ml-0.5">kcal</span>
            )}
          </div>
          {showTarget && (
            <div className={`text-[10px] tabular-nums ${statusColor}`}>
              {isOver
                ? `+${Math.round(delta)} over`
                : `${Math.round(-delta)} left`}
            </div>
          )}
        </div>
      </header>

      {meals.length === 0 ? (
        // No placeholder text — the "+ Add food" button below makes the
        // action self-evident; a "tap the button below" message is just noise.
        <div className="h-1" />
      ) : (
        <ul className="divide-y divide-slate-800/60">
          {meals.map((m) => (
            <li key={m.id}>
              <SwipeableRow
                onDelete={() => onDelete(m.id)}
                onTap={() => navigate(`/meal/${m.id}`)}
                bgClass="bg-slate-900"
              >
                <div className="px-4 py-2.5 flex gap-3 items-center">
              {m.photo_data_url ? (
                <img src={m.photo_data_url} alt="" className="w-11 h-11 rounded-lg object-cover bg-slate-800 flex-shrink-0" />
              ) : (
                <div className="w-11 h-11 rounded-lg bg-slate-800 flex items-center justify-center text-xl flex-shrink-0">
                  {MEAL_TYPE_ICONS[m.meal_type]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-100 truncate">
                  {m.estimate.items.map((i) => i.name).join(", ") || "Untitled"}
                </div>
                {/* Portion line — surfaces the actual measurements ("150 g, 1 cup")
                    on each meal so users can see at a glance how much they logged. */}
                {m.estimate.items.some((i) => i.portion) && (
                  <div className="text-[11px] text-slate-400 truncate mt-0.5">
                    {m.estimate.items.map((i) => i.portion).filter(Boolean).join(" · ")}
                  </div>
                )}
                <div className="flex gap-2 text-[11px] mt-0.5">
                  <span className="text-sky-400">P {m.estimate.totals.protein_g.toFixed(0)}</span>
                  <span className="text-amber-400">C {m.estimate.totals.carbs_g.toFixed(0)}</span>
                  <span className="text-violet-400">F {m.estimate.totals.fat_g.toFixed(0)}</span>
                </div>
              </div>
              <div className="text-right flex-shrink-0 flex items-center gap-1">
                <div className="text-sm font-semibold text-slate-100 tabular-nums mr-1">
                  {Math.round(m.estimate.totals.calories)}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onFavorite(m);
                  }}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-amber-400 hover:bg-slate-800"
                  title="Save as favorite"
                  aria-label="Save as favorite"
                >
                  <Star className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(m.id);
                  }}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-emerald-400 hover:bg-slate-800"
                  title="Edit"
                  aria-label="Edit meal"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
                </div>
              </SwipeableRow>
            </li>
          ))}
        </ul>
      )}

      <div className="px-4 py-2.5 border-t border-slate-800 flex flex-wrap items-center gap-2">
        <button
          onClick={() => onAdd(type)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800 text-emerald-400 text-xs font-medium hover:bg-slate-700 active:bg-slate-700/80"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
          Add food
        </button>

        {/* Share-section chip — only shown when there's something to share
            AND the parent wired up a handler. Tapping fires the bulk-share
            modal owned by the parent. MVP: each item lands as its own
            inbox card on the recipient side; bundle UX is a future PR. */}
        {meals.length > 0 && onShareSection && (
          <button
            type="button"
            onClick={() => onShareSection(meals, `${MEAL_TYPE_LABELS[type]} (${meals.length} ${meals.length === 1 ? "item" : "items"})`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800/60 border border-slate-700/60 text-slate-300 text-xs hover:border-slate-600 hover:text-emerald-400"
            title={`Share ${meals.length === 1 ? "this meal" : "all of " + MEAL_TYPE_LABELS[type].toLowerCase()} with a partner`}
          >
            <Send className="w-3 h-3" strokeWidth={2.5} />
            {meals.length === 1 ? "Share meal" : `Share ${MEAL_TYPE_LABELS[type].toLowerCase()} (${meals.length})`}
          </button>
        )}

        {/* Repeat yesterday's meal of this type — only when there's nothing
            logged here today AND yesterday had something. Massive habit-
            formation aid for routine eaters. */}
        {meals.length === 0 && yesterdayMeal && onLogAgain && (
          <button
            type="button"
            onClick={() => onLogAgain(yesterdayMeal)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800/60 border border-slate-700/60 text-slate-300 text-xs hover:border-slate-600 max-w-full"
          >
            <RotateCcw className="w-3 h-3 flex-shrink-0" strokeWidth={2.5} />
            <span className="truncate">
              Repeat yesterday: <span className="text-slate-100 font-medium">{yesterdayMeal.estimate.items[0]?.name ?? "meal"}</span>
            </span>
          </button>
        )}
      </div>
    </section>
  );
}
